import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/solid";
import {
  CalendarIcon,
  UserIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";
import {
  clearWebinarCertificate,
  requestVerifyWebinarCertificate,
  useWebinarDispatch,
  useWebinarState,
} from "../../Context";
import { getErrorMessages } from "../../Utils";

const CertificateVerify = () => {
  const { t } = useTranslation();
  const location = useLocation();
  // Extract certificate ID from query parameters
  const searchParams = new URLSearchParams(location.search);
  const certificateId = searchParams.get("id");

  const dispatch = useWebinarDispatch();
  const {
    certificateVerification,
    certificateVerificationLoading,
    certificateVerificationError,
  } = useWebinarState();

  const error = getErrorMessages(certificateVerificationError);

  useEffect(() => {
    return () => {
      clearWebinarCertificate(dispatch);
    };
  }, [dispatch]);

  useEffect(() => {
    if (certificateId) {
      requestVerifyWebinarCertificate(dispatch, { id: certificateId });
    }
  }, [certificateId, dispatch]);

  if (certificateVerificationLoading) {
    return (
      <div className="flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-sm p-8 mt-6 mb-0 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
              <svg
                role="status"
                className="w-6 h-6 animate-spin text-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="#E5E7EB"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("certificates.verification.verifying")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("certificates.verification.verifyingMessage")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (certificateVerificationError) {
    return (
      <div className="flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-sm p-8 mt-6 mb-0 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <XCircleIcon className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("certificates.verification.failed")}
            </h3>
            <p className="text-sm text-gray-500 mb-4">{error}</p>
            <button
              onClick={() => {
                window.history.back();
                clearWebinarCertificate(dispatch);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t("certificates.actions.goBack")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center mt-6 mb-0">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-white bg-opacity-20 mb-4">
              <CheckCircleIcon className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {t("certificates.verification.verified")}
            </h1>
            <p className="text-blue-100">
              {t("certificates.verification.validMessage")}
            </p>
          </div>

          {/* Certificate Details */}
          <div className="px-6 py-8">
            <div className="space-y-6">
              {/* Certificate ID */}
              <div className="text-center border-b border-gray-200 pb-6">
                <p className="text-sm text-gray-500 mb-1">
                  {t("certificates.fields.id")}
                </p>
                <p className="text-lg font-mono text-gray-900">
                  {certificateId}
                </p>
              </div>

              {/* Webinar Title */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-6 w-6 text-gray-400 mt-1" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">
                    {t("certificates.fields.webinarTitle")}
                  </p>
                  <p className="text-lg text-gray-900 leading-relaxed">
                    {certificateVerification?.webinarTitle}
                  </p>
                </div>
              </div>

              {/* Date */}
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <CalendarIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">
                    {t("certificates.fields.date")}
                  </p>
                  <p className="text-lg text-gray-900">
                    {new Date(
                      certificateVerification?.webinarSchedule
                    ).toLocaleDateString("id-ID", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>

              {/* Participant Name */}
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <UserIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">
                    {t("certificates.fields.participantName")}
                  </p>
                  <p className="text-lg text-gray-900">
                    {certificateVerification?.name}
                  </p>
                </div>
              </div>

              {/* Signer */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500 mb-2">
                  {t("certificates.fields.signedBy")}
                </p>
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">
                        {certificateVerification.signature?.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-lg font-medium text-gray-900">
                      {certificateVerification.signature?.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {certificateVerification.signature?.jobTitle}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 text-center">
            <p className="text-xs text-gray-500">
              {t("certificates.verification.verifiedOn")}{" "}
              {new Date().toLocaleDateString("id-ID", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateVerify;
